import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import DatePicker, { registerLocale } from 'react-datepicker';
import { format, isBefore, isValid, startOfDay } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';
import { Icon, StatusChip } from '../components/sp-primitives';
import { ConfirmModal, Modal } from '../components/sp-overlays';
import { PShell, SpWorkspaceLoading, useApp } from '../components/sp-shell';
import { isAbortError } from '../services/apiClient';
import { ROUTES } from '../routes';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('pt-BR', ptBR);

function normalizeDoc(v) {
  return String(v || '').replace(/\D+/g, '');
}

function maskCpf(v) {
  const d = normalizeDoc(v).slice(0, 11);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `${d.slice(0, 3)}.${d.slice(3)}`;
  if (d.length <= 9) return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6)}`;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}

function maskCnpj(v) {
  const d = normalizeDoc(v).slice(0, 14);
  if (d.length <= 2) return d;
  if (d.length <= 5) return `${d.slice(0, 2)}.${d.slice(2)}`;
  if (d.length <= 8) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8)}`;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}

function maskDocument(v, byCnpj) {
  return byCnpj ? maskCnpj(v) : maskCpf(v);
}

function parseMoney(v) {
  if (typeof v === 'number') return v;
  const raw = String(v || '').trim();
  if (!raw) return 0;
  const normalized = raw.replace(/[^\d,.-]/g, '').replace(/\./g, '').replace(',', '.');
  const n = Number(normalized);
  return Number.isFinite(n) ? n : 0;
}

function formatMoney(v) {
  const n = Number(v || 0);
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function normalizeForDocumentNumber(value) {
  if (value === undefined || value === null) return '[ausente]';
  const s = String(value).trim();
  return s.length ? s : '[ausente]';
}

function splitCsvLine(line, delimiter) {
  const cols = [];
  let curr = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (ch === '"') {
      if (quoted && line[i + 1] === '"') {
        curr += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if (ch === delimiter && !quoted) {
      cols.push(curr.trim());
      curr = '';
    } else {
      curr += ch;
    }
  }
  cols.push(curr.trim());
  return cols;
}

function inferDelimiter(text) {
  const firstLine = String(text || '').split(/\r?\n/).find((l) => l.trim().length > 0) || '';
  const semicolons = (firstLine.match(/;/g) || []).length;
  const commas = (firstLine.match(/,/g) || []).length;
  return semicolons >= commas ? ';' : ',';
}

function parseBulkFile(fileText, template, skipHeaders = true) {
  const text = String(fileText || '').trim();
  if (!text) return [];
  const delimiter = template?.sep || inferDelimiter(text);
  const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
  if (!lines.length) return [];
  const dataLines = skipHeaders ? lines.slice(1) : lines;
  const headers = skipHeaders
    ? splitCsvLine(lines[0], delimiter).map((h) => h.toLowerCase())
    : splitCsvLine(lines[0], delimiter).map((_, i) => `coluna ${i + 1}`);
  const idx = {
    name: headers.findIndex((h) => h.includes('nome')),
    cpf: headers.findIndex((h) => h === 'cpf' || h.includes('cpf')),
    cnpj: headers.findIndex((h) => h === 'cnpj' || h.includes('cnpj')),
    sienge_totalInvoiceAmount: headers.findIndex((h) => h.includes('valor') || h.includes('sienge_totalInvoiceAmount')),
  };
  return dataLines.map((line, i) => {
    const cols = splitCsvLine(line, delimiter);
    const amount = parseMoney(cols[idx.sienge_totalInvoiceAmount]);
    const row = {
      id: `bulk-${i + 1}`,
      source: 'Em massa',
      creditorName: cols[idx.name] || `Linha ${i + 2}`,
      cpf: normalizeDoc(cols[idx.cpf]),
      cnpj: normalizeDoc(cols[idx.cnpj]),
      amount,
      reference: '',
    };
    // Store raw column values for name-based lookups
    cols.forEach((col, ci) => { row[`_col_${ci}`] = col; });
    return row;
  }).filter((r) => r.amount > 0 || r.cpf || r.cnpj || r.creditorName);
}

const INITIAL_INDIVIDUAL_STEP3_ERRORS = Object.freeze({
  sienge_totalInvoiceAmount: false,
  document: false,
  sienge_documentIdentificationId: false,
  sienge_debtorId: false,
  sienge_costCenterId: false,
  sienge_baseDate: false,
  sienge_dueDate: false,
  sienge_paymentCategoriesId: false,
  sienge_notes: false,
});

/** Input só leitura acionado pelo DatePicker (abre calendário ao clicar/focar). */
const IndividualDueDateInput = React.forwardRef(function IndividualDueDateInput(props, ref) {
  const { value, onClick, className, 'aria-invalid': ariaInvalid, ...rest } = props;
  return (
    <input
      ref={ref}
      type="text"
      value={value || ''}
      onClick={onClick}
      onFocus={onClick}
      readOnly
      className={className}
      aria-invalid={ariaInvalid}
      autoComplete="off"
      {...rest}
    />
  );
});

function getTodayStart() {
  return startOfDay(new Date());
}

function identificationStatus(row, creditorIndex) {
  const hasDoc = Boolean(row.cpf || row.cnpj);
  if (!hasDoc) return { kind: 'amber', label: 'Sem identificador' };
  if (row.cpf && creditorIndex.cpf.has(row.cpf)) return { kind: 'green', label: 'Identificado' };
  if (row.cnpj && creditorIndex.cnpj.has(row.cnpj)) return { kind: 'green', label: 'Identificado' };
  return { kind: 'red', label: 'Não identificado' };
}

/** Valida vínculo com identificação externa no Sienge (`sienge_creditorId` na base sincronizada). */
function siengeIdentificationStatus(row, creditorIndex) {
  const hasDoc = Boolean(row.cpf || row.cnpj);
  if (!hasDoc) return { kind: 'amber', label: 'Sem identificador' };
  const inLocalCpf = Boolean(row.cpf && creditorIndex.cpf?.has(row.cpf));
  const inLocalCnpj = Boolean(row.cnpj && creditorIndex.cnpj?.has(row.cnpj));
  if (!inLocalCpf && !inLocalCnpj) {
    return { kind: 'red', label: 'Não encontrado' };
  }
  const siengeCpf = Boolean(row.cpf && creditorIndex.cpfSienge?.has(row.cpf));
  const siengeCnpj = Boolean(row.cnpj && creditorIndex.cnpjSienge?.has(row.cnpj));
  if (siengeCpf || siengeCnpj) {
    return { kind: 'green', label: 'Identificado no Sienge' };
  }
  return { kind: 'amber', label: 'Sem ID externo' };
}

export function PNewPayment() {
  const app = useApp();
  const [step, setStep] = useState(1);
  const [paymentType, setPaymentType] = useState('');
  const [individualSearch, setIndividualSearch] = useState('');
  const [individualSelectedCreditor, setIndividualSelectedCreditor] = useState(null);
  const [creditorOptions, setCreditorOptions] = useState([]);
  const [individualForm, setIndividualForm] = useState({
    fullName: '',
    sienge_totalInvoiceAmount: '',
    sienge_notes: '',
    document: '',
    sienge_documentIdentificationId: '',
    sienge_debtorId: '',
    sienge_costCenterId: '',
    /** `Date` local ou `null` — Data Base; permite datas passadas. */
    sienge_baseDate: null,
    /** `Date` local ou `null` (obrigatório na etapa 3 individual). */
    sienge_dueDate: null,
    sienge_paymentCategoriesId: '',
  });
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [bulkFile, setBulkFile] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [confirmModal, setConfirmModal] = useState(null);
  const [savingBatch, setSavingBatch] = useState(false);
  const [creditorIndex, setCreditorIndex] = useState({
    cpf: new Set(),
    cnpj: new Set(),
    cpfSienge: new Set(),
    cnpjSienge: new Set(),
    loading: false,
  });
  // Mass step 3: column mapping
  const [massColumnMap, setMassColumnMap] = useState({
    creditorName: '',
    document: '',
    totalInvoiceAmount: '',
    debtorId: '',
    costCenterId: '',
    debtorIdMode: 'id', // 'id' | 'name'
    costCenterIdMode: 'id', // 'id' | 'name'
  });
  const [massCsvHeaders, setMassCsvHeaders] = useState([]);
  const [massCsvPreview, setMassCsvPreview] = useState([]);
  const [massFirstRowIsData, setMassFirstRowIsData] = useState(true);
  const [massCreditorIdMode, setMassCreditorIdMode] = useState('document'); // 'document' | 'name'
  // Mass step 4: global defaults
  const [massForm, setMassForm] = useState({
    sienge_baseDate: null,
    sienge_dueDate: null,
    sienge_paymentCategoriesId: '',
    sienge_documentIdentificationId: '',
    sienge_notes: '',
  });
  const [massStep4Errors, setMassStep4Errors] = useState({
    sienge_baseDate: false,
    sienge_dueDate: false,
    sienge_paymentCategoriesId: false,
    sienge_documentIdentificationId: false,
    sienge_notes: false,
  });
  const [siengeLiveMatch, setSiengeLiveMatch] = useState({
    loading: false,
    checkedDoc: '',
    found: false,
    inactive: false,
  });
  const [siengeInactiveModal, setSiengeInactiveModal] = useState(false);
  const [localLiveMatch, setLocalLiveMatch] = useState({ loading: false, checkedDoc: '', found: false });
  const [individualStep3Errors, setIndividualStep3Errors] = useState(INITIAL_INDIVIDUAL_STEP3_ERRORS);
  const [siengePaymentCategoryOptions, setSiengePaymentCategoryOptions] = useState([]);
  const [siengeDocumentIdentificationOptions, setSiengeDocumentIdentificationOptions] = useState([]);
  const [siengeCompanyOptions, setSiengeCompanyOptions] = useState([]);
  const [siengeCostCenterOptions, setSiengeCostCenterOptions] = useState([]);
  const [siengeFixedFields, setSiengeFixedFields] = useState({ debtorId: '' });
  const fileInputRef = useRef(null);

  const templates = app.templates || [];
  const activeTemplates = templates.filter((t) => t?.ativo !== false);
  const individualTemplates = activeTemplates.filter((t) => t.paymentType === 'individual');
  const massTemplates = activeTemplates.filter((t) => t.paymentType === 'mass');
  const hasIndividual = individualTemplates.length > 0;
  const hasMass = massTemplates.length > 0;
  const availableTemplates = paymentType === 'individual' ? individualTemplates : massTemplates;
  const selectedTemplate = availableTemplates.find((t) => String(t.id) === String(selectedTemplateId));
  const individualByCnpj = paymentType === 'individual' && selectedTemplate?.strat === 'BY_CNPJ';

  useEffect(() => {
    setSelectedTemplateId('');
    setBulkFile(null);
    setPreviewRows([]);
    setIndividualSearch('');
    setIndividualSelectedCreditor(null);
    setIndividualForm({
      fullName: '', sienge_totalInvoiceAmount: '', sienge_notes: '', document: '', sienge_documentIdentificationId: '',
      sienge_debtorId: '', sienge_costCenterId: '', sienge_baseDate: null, sienge_dueDate: null, sienge_paymentCategoriesId: '',
    });
    setIndividualStep3Errors(INITIAL_INDIVIDUAL_STEP3_ERRORS);
  }, [paymentType]);

  useEffect(() => {
    if (step !== 3) setIndividualStep3Errors(INITIAL_INDIVIDUAL_STEP3_ERRORS);
  }, [step]);

  useEffect(() => {
    if ((paymentType !== 'individual' || step !== 3) && (paymentType !== 'mass' || step < 3)) return undefined;
    let mounted = true;
    (async () => {
      try {
        const data = await app.fetchSiengeFieldsOptions();
        const documentIdentifications = data?.effective?.document_identifications;
        const categories = data?.effective?.payment_categories;
        const fixedFields = data?.effective?.fixed_fields;
        if (!mounted) return;
        setSiengePaymentCategoryOptions(Array.isArray(categories) ? categories : []);
        setSiengeDocumentIdentificationOptions(Array.isArray(documentIdentifications) ? documentIdentifications : []);
        setSiengeFixedFields({
          debtorId: String(fixedFields?.debtorId ?? ''),
        });
      } catch (_) {
        if (mounted) {
          setSiengePaymentCategoryOptions([]);
          setSiengeDocumentIdentificationOptions([]);
          setSiengeFixedFields({ debtorId: '' });
        }
      }
    })();
    return () => {
      mounted = false;
    };
  }, [paymentType, step]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((paymentType !== 'individual' || step !== 3) && (paymentType !== 'mass' || step < 3) && !app.activeCompanyId) return undefined;
    if (!app.activeCompanyId) return undefined;
    const ac = new AbortController();
    let mounted = true;
    (async () => {
      try {
        const data = await app.fetchCompanyFieldsCompanies(app.activeCompanyId, { signal: ac.signal });
        if (!mounted) return;
        setSiengeCompanyOptions(Array.isArray(data?.data) ? data.data : []);
      } catch (_) {
        if (mounted) setSiengeCompanyOptions([]);
      }
    })();
    return () => {
      mounted = false;
      ac.abort();
    };
  }, [paymentType, step, app.activeCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((paymentType !== 'individual' || step !== 3) && (paymentType !== 'mass' || step < 3)) return;
    const debtorId = String(siengeFixedFields.debtorId || '').trim();
    if (!debtorId) return;
    if (String(individualForm.sienge_debtorId || '').trim()) return;
    const hasDebtorInOptions = siengeCompanyOptions.some(
      (c) => String(c?.sienge_debtorId || '') === debtorId,
    );
    if (!hasDebtorInOptions) return;
    setIndividualForm((f) => ({
      ...f,
      sienge_debtorId: debtorId,
      sienge_costCenterId: '',
    }));
  }, [paymentType, step, siengeFixedFields.debtorId, siengeCompanyOptions, individualForm.sienge_debtorId]);

  useEffect(() => {
    if ((paymentType !== 'individual' || step !== 3) && (paymentType !== 'mass' || step < 3)) return undefined;
    if (!app.activeCompanyId) return undefined;
    const siengeCompanyId = String(individualForm.sienge_debtorId || '').trim();
    if (!siengeCompanyId) {
      setSiengeCostCenterOptions([]);
      return undefined;
    }
    const ac = new AbortController();
    let mounted = true;
    (async () => {
      try {
        const data = await app.fetchCompanyFieldsCostCenters(
          app.activeCompanyId,
          { sienge_debtorId: siengeCompanyId },
          { signal: ac.signal },
        );
        if (!mounted) return;
        const rows = Array.isArray(data?.data) ? data.data : [];
        setSiengeCostCenterOptions(rows);
        if (!String(individualForm.sienge_costCenterId || '').trim() && rows.length > 0) {
          setIndividualForm((f) => ({
            ...f,
            sienge_costCenterId: String(rows[0]?.sienge_costCenterId || ''),
          }));
        }
      } catch (_) {
        if (mounted) setSiengeCostCenterOptions([]);
      }
    })();
    return () => {
      mounted = false;
      ac.abort();
    };
  }, [paymentType, step, app.activeCompanyId, individualForm.sienge_debtorId]); // eslint-disable-line react-hooks/exhaustive-deps

  const [creditorSearchTerm, setCreditorSearchTerm] = useState('');

  useEffect(() => {
    if ((paymentType !== 'individual' || step !== 3) && (paymentType !== 'mass' || step < 3)) {
      setCreditorOptions([]);
      return undefined;
    }
    if (!app.activeCompanyId) {
      setCreditorOptions([]);
      return undefined;
    }
    const term = creditorSearchTerm.trim();
    if (term.length < 2) { setCreditorOptions([]); return undefined; }
    const ac = new AbortController();
    let mounted = true;
    const timeout = setTimeout(async () => {
      try {
        const data = await app.fetchCompanyCreditors(
          app.activeCompanyId,
          { q: term, per_page: '20', status: 'ativo' },
          { signal: ac.signal }
        );
        if (mounted) setCreditorOptions(data?.data || []);
      } catch (e) {
        if (!isAbortError(e) && mounted) setCreditorOptions([]);
      }
    }, 350);
    return () => {
      mounted = false;
      ac.abort();
      clearTimeout(timeout);
    };
  }, [paymentType, step, app.activeCompanyId, creditorSearchTerm]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    if (!app.workspaceReady) return;
    if (app.companies.length === 0 && app.templates.length === 0) {
      app.navigate(ROUTES.EMPTY, { shell: 'payments' });
    }
  }, [app.workspaceReady, app.companies.length, app.templates.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if ((paymentType !== 'mass' && step !== 4) || (paymentType === 'mass' && step !== 5) || previewRows.length === 0 || !app.activeCompanyId) return undefined;
    const ac = new AbortController();
    let mounted = true;
    (async () => {
      setCreditorIndex({
        cpf: new Set(),
        cnpj: new Set(),
        cpfSienge: new Set(),
        cnpjSienge: new Set(),
        loading: true,
      });
      try {
        const first = await app.fetchCompanyCreditors(app.activeCompanyId, { page: '1', per_page: '200' }, { signal: ac.signal });
        const all = [...(first?.data || [])];
        const pages = Number(first?.last_page || 1);
        for (let p = 2; p <= pages && p <= 5; p += 1) {
          const next = await app.fetchCompanyCreditors(app.activeCompanyId, { page: String(p), per_page: '200' }, { signal: ac.signal });
          all.push(...(next?.data || []));
        }
        const cpf = new Set();
        const cnpj = new Set();
        const cpfSienge = new Set();
        const cnpjSienge = new Set();
        all.forEach((r) => {
          const c = normalizeDoc(r.cpf);
          const n = normalizeDoc(r.cnpj);
          const sid = r.sienge_creditorId != null && String(r.sienge_creditorId).trim() !== '';
          if (c) {
            cpf.add(c);
            if (sid) cpfSienge.add(c);
          }
          if (n) {
            cnpj.add(n);
            if (sid) cnpjSienge.add(n);
          }
        });
        if (mounted) setCreditorIndex({ cpf, cnpj, cpfSienge, cnpjSienge, loading: false });
      } catch (e) {
        if (!isAbortError(e) && mounted) {
          setCreditorIndex({
            cpf: new Set(),
            cnpj: new Set(),
            cpfSienge: new Set(),
            cnpjSienge: new Set(),
            loading: false,
          });
        }
      }
    })();
    return () => {
      mounted = false;
      ac.abort();
    };
  }, [step, previewRows.length, app.activeCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step !== 4 || paymentType !== 'individual' || !app.activeCompanyId || !previewRows.length) {
      setLocalLiveMatch({ loading: false, checkedDoc: '', found: false });
      return undefined;
    }
    const row = previewRows[0] || {};
    const cpf  = normalizeDoc(row.cpf);
    const cnpj = normalizeDoc(row.cnpj);
    const doc  = cpf || cnpj;
    if (!doc) { setLocalLiveMatch({ loading: false, checkedDoc: '', found: false }); return undefined; }
    const ac = new AbortController();
    let mounted = true;
    (async () => {
      setLocalLiveMatch({ loading: true, checkedDoc: doc, found: false });
      try {
        const data = await app.fetchCompanyCreditors(
          app.activeCompanyId,
          { q: doc, per_page: '1' },
          { signal: ac.signal }
        );
        if (!mounted) return;
        setLocalLiveMatch({ loading: false, checkedDoc: doc, found: (data?.total || 0) > 0 });
      } catch (e) {
        if (!isAbortError(e) && mounted) setLocalLiveMatch({ loading: false, checkedDoc: doc, found: false });
      }
    })();
    return () => { mounted = false; ac.abort(); };
  }, [step, paymentType, previewRows, app.activeCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (step !== 4 || paymentType !== 'individual' || !app.activeCompanyId || !previewRows.length) {
      setSiengeLiveMatch({ loading: false, checkedDoc: '', found: false, inactive: false });
      return undefined;
    }
    const row = previewRows[0] || {};
    const cpf = normalizeDoc(row.cpf);
    const cnpj = normalizeDoc(row.cnpj);
    const doc = cpf || cnpj;
    if (!doc) {
      setSiengeLiveMatch({ loading: false, checkedDoc: '', found: false });
      return undefined;
    }

    const ac = new AbortController();
    let mounted = true;
    (async () => {
      setSiengeLiveMatch({ loading: true, checkedDoc: doc, found: false, inactive: false });
      try {
        const data = await app.lookupCompanyCreditorInSienge(
          app.activeCompanyId,
          cpf ? { cpf } : { cnpj },
          { signal: ac.signal },
        );
        if (!mounted) return;
        const found    = Boolean(data?.found);
        const inactive = found && data?.results?.[0]?.active === false;
        setSiengeLiveMatch({ loading: false, checkedDoc: doc, found, inactive });
      } catch (e) {
        if (isAbortError(e) || !mounted) return;
        setSiengeLiveMatch({ loading: false, checkedDoc: doc, found: false, inactive: false });
      }
    })();
    return () => {
      mounted = false;
      ac.abort();
    };
  }, [step, paymentType, previewRows, app.activeCompanyId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (siengeLiveMatch.inactive) setSiengeInactiveModal(true);
  }, [siengeLiveMatch.inactive]);

  if (!app.workspaceReady) {
    return <SpWorkspaceLoading active="payments" title="A carregar novo pagamento…" />;
  }
  if (app.companies.length === 0 && app.templates.length === 0) return null;

  const steps = paymentType === 'mass'
    ? [
        { n: 1, label: 'Tipo' },
        { n: 2, label: 'Modelo' },
        { n: 3, label: 'Upload' },
        { n: 4, label: 'Dados dos Credores' },
        { n: 5, label: 'Pré-visualização' },
      ]
    : [
        { n: 1, label: 'Tipo' },
        { n: 2, label: 'Modelo' },
        { n: 3, label: 'Dados do Credor/Pagamento' },
        { n: 4, label: 'Pré-visualização' },
      ];

  const canGoStep2 = paymentType === 'individual' ? hasIndividual : paymentType === 'mass' ? hasMass : false;

  const nextFromStep3 = async () => {
    if (paymentType === 'individual') {
      const amount = parseMoney(individualForm.sienge_totalInvoiceAmount);
      const doc = normalizeDoc(individualForm.document);
      const dueDateIsPast =
        individualForm.sienge_dueDate
        && isValid(individualForm.sienge_dueDate)
        && isBefore(startOfDay(individualForm.sienge_dueDate), getTodayStart());
      const err = {
        sienge_totalInvoiceAmount: !amount || amount <= 0,
        document:
          !doc
          || (individualByCnpj && doc.length !== 14)
          || (!individualByCnpj && doc.length !== 11),
        sienge_documentIdentificationId: !String(individualForm.sienge_documentIdentificationId || '').trim(),
        sienge_debtorId: !String(individualForm.sienge_debtorId || '').trim(),
        sienge_costCenterId: !String(individualForm.sienge_costCenterId || '').trim(),
        sienge_baseDate: !individualForm.sienge_baseDate || !isValid(individualForm.sienge_baseDate),
        sienge_dueDate: !individualForm.sienge_dueDate || !isValid(individualForm.sienge_dueDate) || dueDateIsPast,
        sienge_paymentCategoriesId: !individualForm.sienge_paymentCategoriesId,
        sienge_notes: !String(individualForm.sienge_notes || '').trim(),
      };
      if (err.sienge_totalInvoiceAmount || err.document || err.sienge_documentIdentificationId || err.sienge_debtorId || err.sienge_costCenterId || err.sienge_baseDate || err.sienge_dueDate || err.sienge_paymentCategoriesId || err.sienge_notes) {
        setIndividualStep3Errors(err);
        const n = Number(err.sienge_totalInvoiceAmount) + Number(err.document) + Number(err.sienge_documentIdentificationId) + Number(err.sienge_debtorId) + Number(err.sienge_costCenterId) + Number(err.sienge_baseDate) + Number(err.sienge_dueDate) + Number(err.sienge_paymentCategoriesId) + Number(err.sienge_notes);
        if (n > 1) {
          const parts = [];
          if (err.sienge_totalInvoiceAmount) parts.push('valor do pagamento');
          if (err.document) parts.push(individualByCnpj ? 'CNPJ' : 'CPF');
          if (err.sienge_documentIdentificationId) parts.push('ID do Documento');
          if (err.sienge_debtorId) parts.push('compania');
          if (err.sienge_costCenterId) parts.push('centro de custo');
          if (err.sienge_baseDate) parts.push('Data Base');
          if (err.sienge_dueDate) parts.push('data de vencimento');
          if (err.sienge_paymentCategoriesId) parts.push('categoria de pagamento');
          if (err.sienge_notes) parts.push('anotações [Sienge]');
          app.toast('error', 'Novo pagamento', `Preencha corretamente: ${parts.join(', ')}.`);
          return;
        }
        if (err.sienge_totalInvoiceAmount) {
          app.toast('error', 'Novo pagamento', 'Informe um valor válido para o pagamento.');
          return;
        }
        if (err.sienge_baseDate) {
          app.toast('error', 'Novo pagamento', 'Informe a Data Base.');
          return;
        }
        if (err.sienge_dueDate) {
          app.toast('error', 'Novo pagamento', 'Informe a data de vencimento (hoje ou futura).');
          return;
        }
        if (err.sienge_paymentCategoriesId) {
          app.toast('error', 'Novo pagamento', 'Selecione a categoria de pagamento.');
          return;
        }
        if (err.sienge_documentIdentificationId) {
          app.toast('error', 'Novo pagamento', 'Selecione a ID do Documento.');
          return;
        }
        if (err.sienge_debtorId) {
          app.toast('error', 'Novo pagamento', 'Selecione a compania.');
          return;
        }
        if (err.sienge_costCenterId) {
          app.toast('error', 'Novo pagamento', 'Selecione o centro de custo.');
          return;
        }
        if (err.sienge_notes) {
          app.toast('error', 'Novo pagamento', 'Preencha as Anotações [Sienge] (máx. 450 caracteres).');
          return;
        }
        if (!doc) {
          app.toast(
            'error',
            'Novo pagamento',
            individualByCnpj ? 'Informe o CNPJ (14 dígitos) ou escolha um credor na busca.' : 'Informe o CPF (11 dígitos) ou escolha um credor na busca.',
          );
        } else if (individualByCnpj) {
          app.toast('error', 'Novo pagamento', 'CNPJ deve ter 14 dígitos.');
        } else {
          app.toast('error', 'Novo pagamento', 'CPF deve ter 11 dígitos.');
        }
        return;
      }
      setIndividualStep3Errors(INITIAL_INDIVIDUAL_STEP3_ERRORS);
      const dueLabel = format(individualForm.sienge_dueDate, 'dd/MM/yyyy');
      const baseFinancialLabel = format(individualForm.sienge_baseDate, 'dd/MM/yyyy');
      setPreviewRows([{
        id: 'single-1',
        source: 'Individual',
        creditorName: String(individualForm.fullName || '').trim() || individualSelectedCreditor?.name || 'Credor informado',
        cpf: individualByCnpj ? '' : doc,
        cnpj: individualByCnpj ? doc : '',
        amount,
        sienge_baseDate: baseFinancialLabel,
        sienge_dueDate: dueLabel,
        sienge_paymentCategoriesId: individualForm.sienge_paymentCategoriesId,
        sienge_documentIdentificationId: individualForm.sienge_documentIdentificationId,
        reference: individualForm.sienge_notes || '',
      }]);
      setStep(4);
      return;
    }

    if (!bulkFile) {
      app.toast('error', 'Novo pagamento', 'Selecione o arquivo para continuar.');
      return;
    }
    setLoadingPreview(true);
    try {
      const text = await bulkFile.text();
      const rows = parseBulkFile(text, selectedTemplate);
      if (!rows.length) {
        app.toast('error', 'Novo pagamento', 'Não foi possível montar o preview com os dados do arquivo.');
        return;
      }
      setPreviewRows(rows);
      setStep(4);
    } finally {
      setLoadingPreview(false);
    }
  };

  const nextFromStep3Mass = async () => {
    if (!bulkFile) {
      app.toast('error', 'Novo pagamento', 'Selecione o arquivo para continuar.');
      return;
    }
    setLoadingPreview(true);
    try {
      const text = await bulkFile.text();
      const rows = parseBulkFile(text, selectedTemplate, !massFirstRowIsData).map((r, i) => {
        // Lookup debtorId/costCenterId by name if needed
        let debtorId = null;
        let costCenterId = null;
        const debtorColIdx = massCsvHeaders.indexOf(massColumnMap.debtorId);
        const costColIdx = massCsvHeaders.indexOf(massColumnMap.costCenterId);
        const debtorRaw = debtorColIdx >= 0 ? r[`_col_${debtorColIdx}`] : null;
        const costRaw = costColIdx >= 0 ? r[`_col_${costColIdx}`] : null;
        if (massColumnMap.debtorIdMode === 'name' && debtorRaw) {
          const match = siengeCompanyOptions.find((c) => String(c?.name || '').toLowerCase() === String(debtorRaw).toLowerCase());
          debtorId = match?.sienge_debtorId || null;
        } else {
          debtorId = debtorRaw || null;
        }
        if (massColumnMap.costCenterIdMode === 'name' && costRaw) {
          const match = siengeCostCenterOptions.find((c) => String(c?.name || '').toLowerCase() === String(costRaw).toLowerCase());
          costCenterId = match?.sienge_costCenterId || null;
        } else {
          costCenterId = costRaw || null;
        }
        return {
          ...r,
          sienge_debtorId: debtorId,
          sienge_costCenterId: costCenterId,
          sienge_baseDate: massForm.sienge_baseDate && isValid(massForm.sienge_baseDate) ? format(massForm.sienge_baseDate, 'dd/MM/yyyy') : null,
          sienge_dueDate: massForm.sienge_dueDate && isValid(massForm.sienge_dueDate) ? format(massForm.sienge_dueDate, 'dd/MM/yyyy') : null,
          sienge_paymentCategoriesId: massForm.sienge_paymentCategoriesId,
          sienge_documentIdentificationId: massForm.sienge_documentIdentificationId,
          sienge_notes: massForm.sienge_notes,
          reference: massForm.sienge_notes || '',
        };
      });
      if (!rows.length) {
        app.toast('error', 'Novo pagamento', 'Não foi possível montar o preview.');
        return;
      }
      setPreviewRows(rows);
      setStep(5);
    } finally {
      setLoadingPreview(false);
    }
  };

  const totalAmount = previewRows.reduce((acc, r) => acc + Number(r.amount || 0), 0);
  const previewFirstRow = previewRows[0] || null;
  const previewSelectedCompany = siengeCompanyOptions.find(
    (c) => String(c?.sienge_debtorId || '') === String(individualForm.sienge_debtorId || ''),
  );
  const previewSelectedCostCenter = siengeCostCenterOptions.find(
    (c) => String(c?.sienge_costCenterId || '') === String(individualForm.sienge_costCenterId || ''),
  );
  const previewLocalStatus = (() => {
    if (!previewFirstRow) return null;
    if (paymentType === 'individual') {
      const doc = normalizeDoc(previewFirstRow?.cpf || previewFirstRow?.cnpj);
      if (!doc) return { kind: 'amber', label: 'Sem identificador' };
      // Se foi selecionado da busca local, está identificado
      if (individualSelectedCreditor) return { kind: 'green', label: 'Identificado' };
      if (localLiveMatch.loading && localLiveMatch.checkedDoc === doc) return { kind: 'amber', label: 'Verificando...' };
      if (!localLiveMatch.loading && localLiveMatch.checkedDoc !== doc) return { kind: 'amber', label: 'Verificando...' };
      return localLiveMatch.found ? { kind: 'green', label: 'Identificado' } : { kind: 'red', label: 'Não identificado' };
    }
    return identificationStatus(previewFirstRow, creditorIndex);
  })();
  const previewSiengeStatus = (() => {
    if (!previewFirstRow) return null;
    if (paymentType !== 'individual') return siengeIdentificationStatus(previewFirstRow, creditorIndex);
    const doc = normalizeDoc(previewFirstRow?.cpf || previewFirstRow?.cnpj);
    if (!doc) return { kind: 'amber', label: 'Sem identificador' };
    if (siengeLiveMatch.loading && siengeLiveMatch.checkedDoc === doc) {
      return { kind: 'amber', label: 'Validando no Sienge...' };
    }
    if (siengeLiveMatch.found && siengeLiveMatch.inactive) return { kind: 'red', label: 'Identificado - Inativo' };
    return siengeLiveMatch.found
      ? { kind: 'green', label: 'Identificado' }
      : { kind: 'red', label: 'Não Identificado' };
  })();
  const hasLocalNotIdentified = paymentType === 'individual' && previewLocalStatus?.kind === 'red';
  const hasSiengeNotIdentified = paymentType === 'individual' && previewSiengeStatus?.kind === 'red';
  const hasIdentificationPending = !creditorIndex.loading && (hasLocalNotIdentified || hasSiengeNotIdentified);
  const needsSaveFromSienge = !creditorIndex.loading && !siengeLiveMatch.loading
    && hasLocalNotIdentified && !hasSiengeNotIdentified;
  const previewAlertMessage = hasLocalNotIdentified && hasSiengeNotIdentified
    ? 'O Credor não foi identificado em nenhum dos sistemas'
    : needsSaveFromSienge
      ? 'O credor será criado na base de dados local se o pagamento for criado'
      : hasLocalNotIdentified
        ? 'O credor não foi identificado no sistema local'
        : hasSiengeNotIdentified
          ? 'O Credor não foi identificado no sistema Sienge'
          : '';

  const confirm = async () => {
    if (!previewRows.length) return;
    if (siengeLiveMatch.inactive) { setSiengeInactiveModal(true); return; }

    let savedCreditorName = null;
    if (paymentType === 'individual' && needsSaveFromSienge) {
      const row = previewRows[0];
      const cpf  = normalizeDoc(row?.cpf);
      const cnpj = normalizeDoc(row?.cnpj);
      try {
        setSavingBatch(true);
        const saved = await app.saveCreditorFromSienge(app.activeCompanyId, {
          ...(cpf  ? { cpf }  : {}),
          ...(cnpj ? { cnpj } : {}),
        });
        savedCreditorName = saved?.name || null;
      } catch (e) {
        app.toast('error', 'Falha ao salvar credor', e?.message || 'Não foi possível salvar o credor no sistema local.');
        setSavingBatch(false);
        return;
      }
    }

    const doSave = async () => {
      if (!app.activeCompanyId) {
        app.toast('error', 'Novo pagamento', 'Selecione uma empresa para salvar o lote.');
        return;
      }
      const templateName = selectedTemplate?.n
        || (paymentType === 'mass' ? 'Modelo em massa' : 'Pagamento individual');
      try {
        setSavingBatch(true);
        const dueDate = individualForm.sienge_dueDate && isValid(individualForm.sienge_dueDate)
          ? format(individualForm.sienge_dueDate, 'yyyy-MM-dd')
          : null;
        const baseFinancialDate = individualForm.sienge_baseDate && isValid(individualForm.sienge_baseDate)
          ? format(individualForm.sienge_baseDate, 'yyyy-MM-dd')
          : null;
        const massDueDate = massForm.sienge_dueDate && isValid(massForm.sienge_dueDate)
          ? format(massForm.sienge_dueDate, 'yyyy-MM-dd')
          : null;
        const massBaseDate = massForm.sienge_baseDate && isValid(massForm.sienge_baseDate)
          ? format(massForm.sienge_baseDate, 'yyyy-MM-dd')
          : null;
        const referenceMonth = paymentType === 'individual' && dueDate
          ? dueDate.slice(0, 7)
          : massDueDate
            ? massDueDate.slice(0, 7)
            : format(new Date(), 'yyyy-MM');
        const documentNumber = paymentType === 'individual'
          ? (() => {
              const creditorId = normalizeForDocumentNumber(individualSelectedCreditor?.sienge_creditorId);
              const timestamp = Math.floor(Date.now() / 1000);
              return `RH-${creditorId}-${timestamp}`;
            })()
          : null;

        const payload = {
          reference_month: referenceMonth,
          source_file: paymentType === 'mass' ? (bulkFile?.name || null) : 'Cadastro manual',
          items_count: previewRows.length,
          total_sienge_totalInvoiceAmount: Number(totalAmount.toFixed(2)),
          template_name: templateName,
          batch_external_id: `P-${Date.now().toString().slice(-6)}`,
          approval_sienge_notes: paymentType === 'individual' ? (individualForm.sienge_notes || null) : (massForm.sienge_notes || null),
        };

        if (paymentType === 'individual') {
          payload.payment = {
            creditor_name: savedCreditorName || previewRows[0]?.creditorName || null,
            document: previewRows[0]?.cpf || previewRows[0]?.cnpj || null,
            document_type: previewRows[0]?.cnpj ? 'cnpj' : 'cpf',
            sienge_paymentCategoriesId: individualForm.sienge_paymentCategoriesId || null,
            sienge_documentIdentificationId: individualForm.sienge_documentIdentificationId || null,
            sienge_debtorId: individualForm.sienge_debtorId || null,
            sienge_costCenterId: individualForm.sienge_costCenterId || null,
            sienge_documentNumber: documentNumber,
            sienge_dueDate: dueDate,
            sienge_baseDate: baseFinancialDate,
            sienge_totalInvoiceAmount: Number(previewRows[0]?.amount || 0),
            sienge_notes: individualForm.sienge_notes || null,
          };
          payload.payments = null;
        } else {
          payload.payment = null;
          payload.payments = previewRows.map((r) => ({
            creditor_name: r.creditorName || null,
            document: r.cpf || r.cnpj || null,
            document_type: r.cnpj ? 'cnpj' : 'cpf',
            sienge_paymentCategoriesId: r.sienge_paymentCategoriesId || massForm.sienge_paymentCategoriesId || null,
            sienge_documentIdentificationId: r.sienge_documentIdentificationId || massForm.sienge_documentIdentificationId || null,
            sienge_debtorId: r.sienge_debtorId || null,
            sienge_costCenterId: r.sienge_costCenterId || null,
            sienge_dueDate: r.sienge_dueDate || massDueDate || null,
            sienge_baseDate: r.sienge_baseDate || massBaseDate || null,
            sienge_totalInvoiceAmount: Number(r.amount || 0),
            sienge_notes: r.sienge_notes || massForm.sienge_notes || null,
          }));
        }

        await app.createBatchDraft(app.activeCompanyId, payload);

        app.toast('success', 'Novo pagamento', `${previewRows.length} pagamento(s) salvo(s) e enviado(s) para aprovação.`);
        app.navigate(ROUTES.PAYMENTS);
      } catch (error) {
        app.toast('error', 'Novo pagamento', error?.message || 'Falha ao salvar no servidor.');
      } finally {
        setSavingBatch(false);
      }
    };

    if (paymentType === 'individual' && hasIdentificationPending) {
      setConfirmModal({
        title: 'Atenção',
        message: 'O Pagamento será criado, contudo, só seguirá para aprovação quando tiver suas pendências solucionadas.',
        confirmLabel: 'Criar mesmo assim',
        onConfirm: doSave,
      });
      return;
    }

    await doSave();
  };

  return (
    <PShell active="payments">
      <div className="sp-screen-head">
        <div>
          <div className="sp-caps sp-screen-head__caps">Pagamentos</div>
          <h1 className="sp-screen-head__title">Novo pagamento</h1>
        </div>
        <button type="button" className="sp-btn sp-btn-ghost" onClick={() => app.navigate(ROUTES.PAYMENTS)}>
          <Icon name="x" size={13} /> Cancelar
        </button>
      </div>

      <div className="sp-import-stepper">
        {steps.map((s) => {
          const active = step === s.n;
          const done = step > s.n;
          return (
            <div key={s.n} className={`sp-import-step${active ? ' is-active' : ''}${done ? ' is-done' : ''}`}>
              <div className="sp-import-step__badge">{done ? <Icon name="check" size={11} stroke={3} /> : s.n}</div>
              <span>{s.label}</span>
            </div>
          );
        })}
      </div>

      {step === 1 && (
        <div className="sp-card sp-newpayment-type-grid">
          <button
            type="button"
            className={`sp-newpayment-type-card${paymentType === 'individual' ? ' is-selected' : ''}`}
            onClick={() => hasIndividual && setPaymentType('individual')}
            disabled={!hasIndividual}
          >
            <div className="sp-newpayment-type-head"><Icon name="user" size={16} /> Individual</div>
            <div className="sp-newpayment-type-desc">Cadastro unitário de pagamento.</div>
            {!hasIndividual && <div className="sp-newpayment-type-note">Sem modelo do tipo Individual.</div>}
          </button>
          <button
            type="button"
            className={`sp-newpayment-type-card${paymentType === 'mass' ? ' is-selected' : ''}`}
            onClick={() => hasMass && setPaymentType('mass')}
            disabled={!hasMass}
          >
            <div className="sp-newpayment-type-head"><Icon name="users" size={16} /> Em massa</div>
            <div className="sp-newpayment-type-desc">Upload de arquivo com múltiplos pagamentos.</div>
            {!hasMass && <div className="sp-newpayment-type-note">Sem modelo do tipo Em massa.</div>}
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="sp-card sp-newpayment-form">
          <div className="sp-newpayment-form__title">Escolha o modelo ({paymentType === 'mass' ? 'Em massa' : 'Individual'})</div>
          <div className="sp-newpayment-type-grid">
            {availableTemplates.map((t) => (
              <button
                key={t.id}
                type="button"
                className={`sp-newpayment-type-card${String(selectedTemplateId) === String(t.id) ? ' is-selected' : ''}`}
                onClick={() => setSelectedTemplateId(String(t.id))}
              >
                <div className="sp-newpayment-type-head">
                  <Icon name={paymentType === 'mass' ? 'users' : 'user'} size={16} /> {t.n}
                </div>
                <div className="sp-newpayment-type-desc">
                  {paymentType === 'mass'
                    ? `${t.fileType || 'CSV'} · ${t.sep || ';'} · ${t.strat === 'BY_CNPJ' ? 'Por CNPJ' : 'Por CPF'}`
                    : `${t.strat === 'BY_CNPJ' ? 'Por CNPJ' : 'Por CPF'}`}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {step === 3 && paymentType === 'individual' && (
        <>
          <div className="sp-card sp-newpayment-form">
            <div className="sp-newpayment-form__title">Dados do Credor</div>
            <div className="sp-field-block">
              <label className="sp-caps sp-label">Busca dinâmica por nome</label>
              <input
                className="sp-input"
                value={individualSearch}
                onChange={(e) => {
                  setIndividualSearch(e.target.value);
                  setCreditorSearchTerm(e.target.value);
                  setIndividualSelectedCreditor(null);
                }}
                placeholder="Digite o nome ou parte do nome do credor"
              />
              {individualSearch.trim().length > 1 && (
                <div className="sp-newpayment-template-card" style={{ marginTop: 8 }}>
                  {(() => {
                    if (!creditorOptions.length) {
                      return (
                        <div className="sp-sidebar-card__muted">
                          Não foram encontrados resultados para a busca: "{individualSearch.trim()}"
                        </div>
                      );
                    }
                    return creditorOptions.map((c) => {
                      const selected = String(individualSelectedCreditor?.id || '') === String(c.id || '');
                      const hasRequiredDoc = individualByCnpj
                        ? Boolean(normalizeDoc(c?.cnpj))
                        : Boolean(normalizeDoc(c?.cpf));
                      return (
                        <button
                          key={String(c.id)}
                          type="button"
                          className={`sp-btn sp-btn-outline sp-btn-tiny sp-newpayment-creditor-btn${selected ? ' is-active' : ''}${hasRequiredDoc ? ' has-doc' : ' missing-doc'}`}
                          style={{ marginRight: 6, marginBottom: 6 }}
                          onClick={async () => {
                            setIndividualSelectedCreditor(c);
                            const d = individualByCnpj ? normalizeDoc(c?.cnpj) : normalizeDoc(c?.cpf);
                            setIndividualForm((f) => ({ ...f, document: d, fullName: String(c?.name || '') }));
                            setIndividualStep3Errors((prev) => ({ ...prev, document: false }));
                            try {
                              const prefs = await app.fetchCreditorPreferences(app.activeCompanyId, c.id);
                              if (prefs?.sienge_debtorId || prefs?.sienge_costCenterId) {
                                setIndividualForm((f) => ({
                                  ...f,
                                  ...(prefs.sienge_debtorId     ? { sienge_debtorId:    String(prefs.sienge_debtorId) }     : {}),
                                  ...(prefs.sienge_costCenterId ? { sienge_costCenterId: String(prefs.sienge_costCenterId) } : {}),
                                }));
                              }
                            } catch { /* sem preferências salvas — ignora */ }
                          }}
                        >
                          {c.name}
                        </button>
                      );
                    });
                  })()}
                </div>
              )}
            </div>
            <div className="sp-newpayment-grid">
              <div className="sp-field-block">
                <label className="sp-caps sp-label">Nome Completo</label>
                <input
                  className="sp-input"
                  value={individualForm.fullName}
                  onChange={(e) => {
                    setIndividualForm((f) => ({ ...f, fullName: e.target.value }));
                  }}
                  placeholder="Nome completo do credor"
                  autoComplete="off"
                />
              </div>
              <div className={`sp-field-block${individualStep3Errors.document ? ' sp-field-block--invalid' : ''}`}>
                <label className="sp-caps sp-label">{individualByCnpj ? 'CNPJ' : 'CPF'}</label>
                <input
                  className={`sp-input sp-mono${individualStep3Errors.document ? ' is-invalid' : ''}`}
                  value={maskDocument(individualForm.document, individualByCnpj)}
                  onChange={(e) => {
                    const digits = normalizeDoc(e.target.value).slice(0, individualByCnpj ? 14 : 11);
                    setIndividualForm((f) => ({ ...f, document: digits }));
                    setIndividualStep3Errors((prev) => ({ ...prev, document: false }));
                  }}
                  placeholder={individualByCnpj ? '14 dígitos ou resultado da busca' : '11 dígitos ou resultado da busca'}
                  autoComplete="off"
                  aria-invalid={individualStep3Errors.document}
                />
              </div>
            </div>
          </div>

          <div className="sp-card sp-newpayment-form">
            <div className="sp-newpayment-form__title">Dados de Pagamento</div>
            <div className="sp-newpayment-grid">
              <div className={`sp-field-block sp-field-block--full${individualStep3Errors.sienge_totalInvoiceAmount ? ' sp-field-block--invalid' : ''}`}>
                <label className="sp-caps sp-label">
                  Valor do Pagamento <span className="sp-sienge-field-id">[TotalInvoiceAmount]</span>
                </label>
                <input
                  className={`sp-input sp-mono${individualStep3Errors.sienge_totalInvoiceAmount ? ' is-invalid' : ''}`}
                  value={individualForm.sienge_totalInvoiceAmount}
                  onChange={(e) => {
                    setIndividualForm((f) => ({ ...f, sienge_totalInvoiceAmount: e.target.value }));
                    setIndividualStep3Errors((prev) => ({ ...prev, sienge_totalInvoiceAmount: false }));
                  }}
                  placeholder="Ex.: 1500,00"
                  aria-invalid={individualStep3Errors.sienge_totalInvoiceAmount}
                />
              </div>

              <div className="sp-newpayment-row">
                <fieldset className="sp-fieldset">
                  <legend className="sp-legend">Pagador</legend>
                  <div className={`sp-field-block${individualStep3Errors.sienge_debtorId ? ' sp-field-block--invalid' : ''}`}>
                    <label className="sp-caps sp-label">
                      Companhia <span className="sp-sienge-field-id">[DebtorId]</span>
                    </label>
                    <select
                      className={`sp-input${individualStep3Errors.sienge_debtorId ? ' is-invalid' : ''}`}
                      value={individualForm.sienge_debtorId || ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        setIndividualForm((f) => ({ ...f, sienge_debtorId: val, sienge_costCenterId: '' }));
                        setIndividualStep3Errors((prev) => ({ ...prev, sienge_debtorId: false, sienge_costCenterId: false }));
                      }}
                      aria-invalid={individualStep3Errors.sienge_debtorId}
                    >
                      <option value="">Selecione a Companhia</option>
                      {siengeCompanyOptions.map((opt) => {
                        const cid = opt?.sienge_debtorId || opt?.id || '';
                        return (
                          <option key={String(cid)} value={String(cid)}>
                            {`${String(opt?.name || '')} (id=${cid})`}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div className={`sp-field-block${individualStep3Errors.sienge_costCenterId ? ' sp-field-block--invalid' : ''}`}>
                    <label className="sp-caps sp-label">
                      Centro de Custo <span className="sp-sienge-field-id">[CostCenterId]</span>
                    </label>
                    <select
                      className={`sp-input${individualStep3Errors.sienge_costCenterId ? ' is-invalid' : ''}`}
                      value={individualForm.sienge_costCenterId || ''}
                      onChange={(e) => {
                        setIndividualForm((f) => ({ ...f, sienge_costCenterId: e.target.value }));
                        setIndividualStep3Errors((prev) => ({ ...prev, sienge_costCenterId: false }));
                      }}
                      disabled={!String(individualForm.sienge_debtorId || '').trim()}
                      aria-invalid={individualStep3Errors.sienge_costCenterId}
                    >
                      <option value="">Selecione o Centro de Custo</option>
                      {siengeCostCenterOptions.map((opt) => {
                        const cid = opt?.sienge_costCenterId || opt?.id || '';
                        return (
                          <option key={String(cid)} value={String(cid)}>
                            {`${String(opt?.name || '')} (id=${cid})`}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </fieldset>

                <fieldset className="sp-fieldset">
                  <legend className="sp-legend">Datas</legend>
                  <div className={`sp-field-block${individualStep3Errors.sienge_baseDate ? ' sp-field-block--invalid' : ''}`}>
                    <label className="sp-caps sp-label">
                      Data Base <span className="sp-sienge-field-id">[BaseDate]</span>
                    </label>
                    <div className="sp-newpayment-datepicker-wrap">
                      <DatePicker
                        selected={individualForm.sienge_baseDate}
                        onChange={(date) => {
                          setIndividualForm((f) => ({ ...f, sienge_baseDate: date }));
                          setIndividualStep3Errors((prev) => ({ ...prev, sienge_baseDate: false }));
                        }}
                        locale="pt-BR"
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Selecione no calendário"
                        calendarStartDay={1}
                        customInput={(
                          <IndividualDueDateInput
                            className={`sp-input sp-mono${individualStep3Errors.sienge_baseDate ? ' is-invalid' : ''}`}
                            aria-invalid={individualStep3Errors.sienge_baseDate}
                          />
                        )}
                        popperClassName="sp-newpayment-datepicker-popper"
                      />
                    </div>
                  </div>
                  <div className={`sp-field-block${individualStep3Errors.sienge_dueDate ? ' sp-field-block--invalid' : ''}`}>
                    <label className="sp-caps sp-label">
                      Data de vencimento <span className="sp-sienge-field-id">[DueDate]</span>
                    </label>
                    <div className="sp-newpayment-datepicker-wrap">
                      <DatePicker
                        selected={individualForm.sienge_dueDate}
                        onChange={(date) => {
                          setIndividualForm((f) => ({ ...f, sienge_dueDate: date }));
                          setIndividualStep3Errors((prev) => ({ ...prev, sienge_dueDate: false }));
                        }}
                        locale="pt-BR"
                        dateFormat="dd/MM/yyyy"
                        placeholderText="Selecione no calendário"
                        calendarStartDay={1}
                        minDate={getTodayStart()}
                        customInput={(
                          <IndividualDueDateInput
                            className={`sp-input sp-mono${individualStep3Errors.sienge_dueDate ? ' is-invalid' : ''}`}
                            aria-invalid={individualStep3Errors.sienge_dueDate}
                          />
                        )}
                        popperClassName="sp-newpayment-datepicker-popper"
                      />
                    </div>
                  </div>
                </fieldset>
              </div>

              <div className="sp-newpayment-row">
                <fieldset className="sp-fieldset">
                  <legend className="sp-legend">Tipo</legend>
                  <div className={`sp-field-block${individualStep3Errors.sienge_paymentCategoriesId ? ' sp-field-block--invalid' : ''}`}>
                    <label className="sp-caps sp-label">
                      Categoria <span className="sp-sienge-field-id">[PaymentCategoriesId]</span>
                    </label>
                    <select
                      className={`sp-input${individualStep3Errors.sienge_paymentCategoriesId ? ' is-invalid' : ''}`}
                      value={individualForm.sienge_paymentCategoriesId}
                      onChange={(e) => {
                        setIndividualForm((f) => ({ ...f, sienge_paymentCategoriesId: e.target.value }));
                        setIndividualStep3Errors((prev) => ({ ...prev, sienge_paymentCategoriesId: false }));
                      }}
                      aria-invalid={individualStep3Errors.sienge_paymentCategoriesId}
                    >
                      <option value="">Selecione</option>
                      {siengePaymentCategoryOptions.map((opt) => (
                        <option key={String(opt?.value || '')} value={String(opt?.value || '')}>
                          {`${String(opt?.name || '')}${String(opt?.value || '').trim() ? ` (${String(opt?.value || '')})` : ''}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className={`sp-field-block${individualStep3Errors.sienge_documentIdentificationId ? ' sp-field-block--invalid' : ''}`}>
                    <label className="sp-caps sp-label">
                      ID do Documento <span className="sp-sienge-field-id">[DocumentIdentificationId]</span>
                    </label>
                    <select
                      className={`sp-input${individualStep3Errors.sienge_documentIdentificationId ? ' is-invalid' : ''}`}
                      value={individualForm.sienge_documentIdentificationId}
                      onChange={(e) => {
                        setIndividualForm((f) => ({ ...f, sienge_documentIdentificationId: e.target.value }));
                        setIndividualStep3Errors((prev) => ({ ...prev, sienge_documentIdentificationId: false }));
                      }}
                      aria-invalid={individualStep3Errors.sienge_documentIdentificationId}
                    >
                      <option value="">Selecione</option>
                      {siengeDocumentIdentificationOptions.map((opt) => (
                        <option key={String(opt?.value || '')} value={String(opt?.value || '')}>
                          {`${String(opt?.name || '')}${String(opt?.value || '').trim() ? ` (${String(opt?.value || '')})` : ''}`}
                        </option>
                      ))}
                    </select>
                  </div>
                </fieldset>

                <fieldset className="sp-fieldset sp-fieldset--single">
                  <legend className="sp-legend">Anotações</legend>
                  <div className={`sp-field-block${individualStep3Errors.sienge_notes ? ' sp-field-block--invalid' : ''}`}>
                    <textarea
                      className={`sp-input${individualStep3Errors.sienge_notes ? ' is-invalid' : ''}`}
                      value={individualForm.sienge_notes}
                      onChange={(e) => {
                        const val = e.target.value.slice(0, 450);
                        setIndividualForm((f) => ({ ...f, sienge_notes: val }));
                        setIndividualStep3Errors((prev) => ({ ...prev, sienge_notes: false }));
                      }}
                      placeholder="Anotações obrigatórias para envio ao Sienge"
                      maxLength={450}
                      rows={3}
                      aria-invalid={individualStep3Errors.sienge_notes}
                    />
                    <div className="sp-char-counter">
                      {String(individualForm.sienge_notes || '').length}/450
                    </div>
                  </div>
                </fieldset>
              </div>
            </div>
          </div>
        </>
      )}

      {step === 3 && paymentType === 'mass' && (
        <>
          <div className="sp-newpayment-row">
            <div className="sp-card sp-newpayment-form">
              <div className="sp-newpayment-form__title">Upload</div>
              <div className="sp-field-block">
                <input ref={fileInputRef} type="file" accept=".csv,.txt" className="sp-hidden-input" onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  setBulkFile(file);
                  if (file) {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const text = String(ev.target.result || '');
                      const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
                      if (lines.length > 0) {
                        const delimiter = selectedTemplate?.sep || (text.includes(';') ? ';' : ',');
                        const firstRow = splitCsvLine(lines[0], delimiter);
                        const headers = massFirstRowIsData
                          ? firstRow.map((_, i) => `Coluna ${i + 1}`)
                          : firstRow;
                        setMassCsvHeaders(headers);
                        const previewStart = massFirstRowIsData ? 0 : 1;
                        const preview = lines.slice(previewStart, previewStart + 6).map((line) => splitCsvLine(line, delimiter));
                        setMassCsvPreview(preview);
                      }
                    };
                    reader.readAsText(file);
                  }
                }} />
                <button type="button" className={`sp-btn sp-btn-outline sp-btn-block${bulkFile ? ' sp-btn-file-selected' : ' sp-btn-file-empty'}`} onClick={() => fileInputRef.current?.click()} style={{ borderWidth: 2 }}>
                  <Icon name="upload" size={13} /> {bulkFile ? bulkFile.name : 'Selecionar arquivo'}
                </button>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, fontSize: 12, cursor: 'pointer' }}>
                  <input type="checkbox" checked={massFirstRowIsData} onChange={(e) => setMassFirstRowIsData(e.target.checked)} style={{ cursor: 'pointer' }} />
                  A primeira linha contém valores
                </label>
              </div>
            </div>
            {bulkFile && massCsvHeaders.length > 0 && (
              <div className="sp-card sp-newpayment-form">
                <div className="sp-newpayment-form__title">Campo de identificação do Credor</div>
                <div className="sp-toggle-switch" style={{ justifyContent: 'center', padding: '8px 0' }} onClick={() => setMassCreditorIdMode((m) => m === 'document' ? 'name' : 'document')}>
                  <span className={`sp-toggle-switch__label${massCreditorIdMode === 'name' ? ' sp-toggle-switch__label--active' : ''}`}>Nome do Credor</span>
                  <span className="sp-toggle-switch__track">
                    <span className={`sp-toggle-switch__thumb${massCreditorIdMode === 'document' ? ' sp-toggle-switch__thumb--right' : ''}`} />
                  </span>
                  <span className={`sp-toggle-switch__label${massCreditorIdMode === 'document' ? ' sp-toggle-switch__label--active' : ''}`}>CPF/CNPJ</span>
                </div>
              </div>
            )}
          </div>
          {bulkFile && massCsvHeaders.length > 0 && (
            <div className="sp-card sp-newpayment-form">
              <div className="sp-newpayment-form__title">Mapeamento de Colunas</div>
              <div className="sp-newpayment-grid" style={{ padding: 0 }}>
                <div className="sp-newpayment-row">
                  <fieldset className="sp-fieldset">
                    <legend className="sp-legend">Pagamentos e Credores</legend>
                    <div className="sp-field-block sp-field-block--full">
                      <label className="sp-caps sp-label">Valor <span className="sp-sienge-field-id">[TotalInvoiceAmount]</span></label>
                      <select className="sp-input" value={massColumnMap.totalInvoiceAmount} onChange={(e) => setMassColumnMap((m) => ({ ...m, totalInvoiceAmount: e.target.value }))}>
                        <option value="">Selecione a coluna</option>
                        {massCsvHeaders.map((h, i) => {
                          const sample = massCsvPreview[1]?.[i] || massCsvPreview[0]?.[i] || '';
                          return <option key={i} value={h}>{sample ? `${h} — ${sample}` : h}</option>;
                        })}
                      </select>
                    </div>
                    <div className="sp-field-block">
                      <label className="sp-caps sp-label">Nome do Credor</label>
                      <select className="sp-input" value={massColumnMap.creditorName} onChange={(e) => setMassColumnMap((m) => ({ ...m, creditorName: e.target.value }))} disabled={massCreditorIdMode === 'document'}>
                        <option value="">Selecione a coluna</option>
                        {massCsvHeaders.map((h, i) => {
                          const sample = massCsvPreview[1]?.[i] || massCsvPreview[0]?.[i] || '';
                          return <option key={i} value={h}>{sample ? `${h} — ${sample}` : h}</option>;
                        })}
                      </select>
                    </div>
                    <div className="sp-field-block">
                      <label className="sp-caps sp-label">CPF/CNPJ</label>
                      <select className="sp-input" value={massColumnMap.document} onChange={(e) => setMassColumnMap((m) => ({ ...m, document: e.target.value }))} disabled={massCreditorIdMode === 'name'}>
                        <option value="">Selecione a coluna</option>
                        {massCsvHeaders.map((h, i) => {
                          const sample = massCsvPreview[1]?.[i] || massCsvPreview[0]?.[i] || '';
                          return <option key={i} value={h}>{sample ? `${h} — ${sample}` : h}</option>;
                        })}
                      </select>
                    </div>
                  </fieldset>
                  <fieldset className="sp-fieldset sp-fieldset--single">
                    <legend className="sp-legend">Pagador</legend>
                    {[
                      { key: 'debtorId', modeKey: 'debtorIdMode' },
                      { key: 'costCenterId', modeKey: 'costCenterIdMode' },
                    ].map((field) => (
                      <div key={field.key} className="sp-field-block">
                        <label className="sp-caps sp-label">
                          {field.key === 'debtorId' ? <>Companhia <span className="sp-sienge-field-id">[DebtorId]</span></> : <>Centro de Custo <span className="sp-sienge-field-id">[CostCenterId]</span></>}
                        </label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <select className="sp-input" style={{ flex: 1 }} value={massColumnMap[field.key]} onChange={(e) => setMassColumnMap((m) => ({ ...m, [field.key]: e.target.value }))}>
                            <option value="">Selecione a coluna</option>
                            {massCsvHeaders.map((h, i) => {
                              const sample = massCsvPreview[1]?.[i] || massCsvPreview[0]?.[i] || '';
                              return <option key={i} value={h}>{sample ? `${h} — ${sample}` : h}</option>;
                            })}
                          </select>
                          <div className="sp-toggle-switch" onClick={() => setMassColumnMap((m) => ({ ...m, [field.modeKey]: m[field.modeKey] === 'id' ? 'name' : 'id' }))}>
                            <span className={`sp-toggle-switch__label${massColumnMap[field.modeKey] === 'name' ? ' sp-toggle-switch__label--active' : ''}`}>Nome</span>
                            <span className="sp-toggle-switch__track">
                              <span className={`sp-toggle-switch__thumb${massColumnMap[field.modeKey] === 'id' ? ' sp-toggle-switch__thumb--right' : ''}`} />
                            </span>
                            <span className={`sp-toggle-switch__label${massColumnMap[field.modeKey] === 'id' ? ' sp-toggle-switch__label--active' : ''}`}>Id.</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </fieldset>
                </div>
              </div>
              {massCsvPreview.length > 0 && (
                <div className="sp-newpayment-template-card" style={{ marginTop: 12 }}>
                  <div className="sp-caps" style={{ marginBottom: 8 }}>Preview (primeiras linhas)</div>
                  <table className="sp-table">
                    <thead><tr>{massCsvHeaders.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
                    <tbody>
                      {massCsvPreview.map((row, ri) => <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>)}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {step === 4 && paymentType === 'mass' && (
        <div className="sp-card sp-newpayment-form">
          <div className="sp-newpayment-form__title">Dados dos Credores</div>
          <div className="sp-newpayment-grid">
            <div className={`sp-field-block${massStep4Errors.sienge_baseDate ? ' sp-field-block--invalid' : ''}`}>
              <label className="sp-caps sp-label">
                Data Base <span className="sp-sienge-field-id">[BaseDate]</span>
              </label>
              <div className="sp-newpayment-datepicker-wrap">
                <DatePicker
                  selected={massForm.sienge_baseDate}
                  onChange={(date) => {
                    setMassForm((f) => ({ ...f, sienge_baseDate: date }));
                    setMassStep4Errors((prev) => ({ ...prev, sienge_baseDate: false }));
                  }}
                  locale="pt-BR"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Selecione no calendário"
                  calendarStartDay={1}
                  customInput={<IndividualDueDateInput className={`sp-input sp-mono${massStep4Errors.sienge_baseDate ? ' is-invalid' : ''}`} aria-invalid={massStep4Errors.sienge_baseDate} />}
                  popperClassName="sp-newpayment-datepicker-popper"
                />
              </div>
            </div>
            <div className={`sp-field-block${massStep4Errors.sienge_dueDate ? ' sp-field-block--invalid' : ''}`}>
              <label className="sp-caps sp-label">
                Data de vencimento <span className="sp-sienge-field-id">[DueDate]</span>
              </label>
              <div className="sp-newpayment-datepicker-wrap">
                <DatePicker
                  selected={massForm.sienge_dueDate}
                  onChange={(date) => {
                    setMassForm((f) => ({ ...f, sienge_dueDate: date }));
                    setMassStep4Errors((prev) => ({ ...prev, sienge_dueDate: false }));
                  }}
                  locale="pt-BR"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Selecione no calendário"
                  calendarStartDay={1}
                  minDate={getTodayStart()}
                  customInput={<IndividualDueDateInput className={`sp-input sp-mono${massStep4Errors.sienge_dueDate ? ' is-invalid' : ''}`} aria-invalid={massStep4Errors.sienge_dueDate} />}
                  popperClassName="sp-newpayment-datepicker-popper"
                />
              </div>
            </div>
            <div className={`sp-field-block${massStep4Errors.sienge_paymentCategoriesId ? ' sp-field-block--invalid' : ''}`}>
              <label className="sp-caps sp-label">
                Categoria <span className="sp-sienge-field-id">[PaymentCategoriesId]</span>
              </label>
              <select className={`sp-input${massStep4Errors.sienge_paymentCategoriesId ? ' is-invalid' : ''}`} value={massForm.sienge_paymentCategoriesId} onChange={(e) => { setMassForm((f) => ({ ...f, sienge_paymentCategoriesId: e.target.value })); setMassStep4Errors((prev) => ({ ...prev, sienge_paymentCategoriesId: false })); }}>
                <option value="">Selecione</option>
                {siengePaymentCategoryOptions.map((opt) => <option key={String(opt?.value || '')} value={String(opt?.value || '')}>{`${String(opt?.name || '')}${String(opt?.value || '').trim() ? ` (${String(opt?.value || '')})` : ''}`}</option>)}
              </select>
            </div>
            <div className={`sp-field-block${massStep4Errors.sienge_documentIdentificationId ? ' sp-field-block--invalid' : ''}`}>
              <label className="sp-caps sp-label">
                ID do Documento <span className="sp-sienge-field-id">[DocumentIdentificationId]</span>
              </label>
              <select className={`sp-input${massStep4Errors.sienge_documentIdentificationId ? ' is-invalid' : ''}`} value={massForm.sienge_documentIdentificationId} onChange={(e) => { setMassForm((f) => ({ ...f, sienge_documentIdentificationId: e.target.value })); setMassStep4Errors((prev) => ({ ...prev, sienge_documentIdentificationId: false })); }}>
                <option value="">Selecione</option>
                {siengeDocumentIdentificationOptions.map((opt) => <option key={String(opt?.value || '')} value={String(opt?.value || '')}>{`${String(opt?.name || '')}${String(opt?.value || '').trim() ? ` (${String(opt?.value || '')})` : ''}`}</option>)}
              </select>
            </div>
            <div className={`sp-field-block sp-field-block--full${massStep4Errors.sienge_notes ? ' sp-field-block--invalid' : ''}`}>
              <label className="sp-caps sp-label">Anotações [Sienge]</label>
              <textarea className={`sp-input${massStep4Errors.sienge_notes ? ' is-invalid' : ''}`} value={massForm.sienge_notes} onChange={(e) => { const val = e.target.value.slice(0, 450); setMassForm((f) => ({ ...f, sienge_notes: val })); setMassStep4Errors((prev) => ({ ...prev, sienge_notes: false })); }} placeholder="Anotações obrigatórias para envio ao Sienge" maxLength={450} rows={3} aria-invalid={massStep4Errors.sienge_notes} />
              <div className="sp-char-counter">{String(massForm.sienge_notes || '').length}/450</div>
            </div>
          </div>
        </div>
      )}

      {step === (paymentType === 'mass' ? 5 : 4) && (
        <>
          <div className="sp-card sp-card-flush">
            {paymentType === 'individual' && hasIdentificationPending && (
              <div className="sp-newpayment-pending-alert">{previewAlertMessage}</div>
            )}
            <div className="sp-newpayment-preview-head">
              <div>
                <div className="sp-newpayment-form__title">Preview para confirmação</div>
                <div className="sp-newpayment-preview-total">Total: {formatMoney(totalAmount)} · {previewRows.length} pagamento(s)</div>
              </div>
              {creditorIndex.loading ? <span className="sp-sidebar-card__muted">Validando credores...</span> : null}
            </div>
            <table className="sp-table">
              <thead>
                <tr>
                  <th>Credor</th>
                  <th>Documento</th>
                  {paymentType !== 'mass' && <><th>Tipo</th><th className="sp-mono">Data base</th><th className="sp-mono">Vencimento</th><th className="sp-th-num">Valor</th></>}
                  <th>Status (Local)</th>
                  <th>STATUS (Sienge)</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((r) => {
                  const st = (paymentType === 'individual' && previewLocalStatus) ? previewLocalStatus : identificationStatus(r, creditorIndex);
                  const stSienge = paymentType === 'individual' ? previewSiengeStatus : siengeIdentificationStatus(r, creditorIndex);
                  return (
                    <tr key={r.id}>
                      <td>{r.creditorName || '—'}</td>
                      <td className="sp-mono">{maskDocument(r.cpf || r.cnpj, !!r.cnpj)}</td>
                      {paymentType !== 'mass' && <><td>{(siengePaymentCategoryOptions.find((o) => String(o.value) === String(r.sienge_paymentCategoriesId)) || {}).name || r.sienge_paymentCategoriesId || '—'}</td><td className="sp-mono">{r.sienge_baseDate || '—'}</td><td className="sp-mono">{r.sienge_dueDate || '—'}</td><td className="sp-num sp-td-num">{formatMoney(r.amount)}</td></>}
                      <td><StatusChip kind={st.kind}>{st.label}</StatusChip></td>
                      <td>
                        {creditorIndex.loading || (paymentType === 'individual' && siengeLiveMatch.loading) ? (
                          <span className="sp-sidebar-card__muted">…</span>
                        ) : (
                        <StatusChip kind={stSienge?.kind || 'neutral'}>{stSienge?.label || '—'}</StatusChip>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="sp-card sp-newpayment-form">
            <div className="sp-newpayment-form__title">Outros dados do pagamento</div>
            <div className="sp-newpayment-grid">
              {paymentType === 'individual' && (
                <>
                  <div className="sp-field-block">
                    <label className="sp-caps sp-label">ID do Documento</label>
                    <div className="sp-input sp-input-readonly">
                      {(() => {
                        const opt = siengeDocumentIdentificationOptions.find(
                          (x) => String(x?.value || '') === String(individualForm.sienge_documentIdentificationId || ''),
                        );
                        return opt
                          ? `${String(opt?.name || '')}${String(opt?.value || '').trim() ? ` (${String(opt?.value || '')})` : ''}`
                          : '—';
                      })()}
                    </div>
                  </div>
                  <div className="sp-field-block">
                    <label className="sp-caps sp-label">Número do Documento</label>
                    <div className="sp-input sp-input-readonly">
                      {(() => {
                        const creditorId = normalizeForDocumentNumber(individualSelectedCreditor?.sienge_creditorId);
                        const timestamp = Math.floor(Date.now() / 1000);
                        return `RH-${creditorId}-${timestamp}`;
                      })()}
                    </div>
                  </div>
                  <div className="sp-field-block">
                    <label className="sp-caps sp-label">Companhia</label>
                    <div className="sp-input sp-input-readonly">{previewSelectedCompany?.name || '—'}</div>
                  </div>
                  <div className="sp-field-block">
                    <label className="sp-caps sp-label">Centro de Custo</label>
                    <div className="sp-input sp-input-readonly">{previewSelectedCostCenter?.name || '—'}</div>
                  </div>
                  <div className="sp-field-block">
                    <label className="sp-caps sp-label">Anotações [Sienge]</label>
                    <div className="sp-input sp-input-readonly">{individualForm.sienge_notes || '—'}</div>
                  </div>
                </>
              )}
              {paymentType === 'mass' && (
                <div className="sp-field-block">
                  <label className="sp-caps sp-label">Arquivo de origem</label>
                  <div className="sp-input sp-input-readonly">{bulkFile?.name || '—'}</div>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <div className="sp-import-footer">
        <button type="button" className="sp-btn sp-btn-outline" disabled={step === 1 || loadingPreview} onClick={() => setStep((s) => Math.max(1, s - 1))}>
          <span className="sp-icon--flip"><Icon name="arrow" size={13} /></span> Voltar
        </button>
        {step < (paymentType === 'mass' ? 5 : 4) ? (
          <button
            type="button"
            className="sp-btn sp-btn-accent"
            disabled={
              loadingPreview || savingBatch
              || (step === 1 && !canGoStep2)
              || (step === 2 && !selectedTemplateId)
              || (step === 3 && paymentType === 'mass' && (!bulkFile || (massCreditorIdMode === 'document' && !massColumnMap.document) || (massCreditorIdMode === 'name' && !massColumnMap.creditorName) || !massColumnMap.totalInvoiceAmount || !massColumnMap.debtorId || !massColumnMap.costCenterId))
            }
            onClick={() => {
              if (step === 1) {
                setStep(2);
                return;
              }
              if (step === 2) {
                if (!selectedTemplateId) {
                  app.toast('error', 'Novo pagamento', 'Escolha um modelo para continuar.');
                  return;
                }
                setStep(3);
                return;
              }
              if (step === 3 && paymentType === 'mass') {
                const docOk = massCreditorIdMode === 'document' ? massColumnMap.document : massColumnMap.creditorName;
                if (!bulkFile || !docOk || !massColumnMap.totalInvoiceAmount || !massColumnMap.debtorId || !massColumnMap.costCenterId) {
                  app.toast('error', 'Novo pagamento', 'Mapeie todas as colunas obrigatórias para continuar.');
                  return;
                }
                setStep(4);
                return;
              }
              if (step === 4 && paymentType === 'mass') {
                const errs = {};
                errs.sienge_dueDate = !massForm.sienge_dueDate || !isValid(massForm.sienge_dueDate);
                errs.sienge_baseDate = !massForm.sienge_baseDate || !isValid(massForm.sienge_baseDate);
                errs.sienge_paymentCategoriesId = !massForm.sienge_paymentCategoriesId;
                errs.sienge_documentIdentificationId = !massForm.sienge_documentIdentificationId;
                errs.sienge_notes = !String(massForm.sienge_notes || '').trim();
                if (errs.sienge_dueDate || errs.sienge_baseDate || errs.sienge_paymentCategoriesId || errs.sienge_documentIdentificationId || errs.sienge_notes) {
                  setMassStep4Errors(errs);
                  app.toast('error', 'Novo pagamento', 'Preencha todos os campos obrigatórios.');
                  return;
                }
                setMassStep4Errors({ sienge_baseDate: false, sienge_dueDate: false, sienge_paymentCategoriesId: false, sienge_documentIdentificationId: false, sienge_notes: false });
                nextFromStep3Mass();
                return;
              }
              nextFromStep3();
            }}
          >
            {loadingPreview ? 'Gerando preview…' : 'Próxima etapa'} <Icon name="arrow" size={13} />
          </button>
        ) : (
          <button
            type="button"
            className="sp-btn sp-btn-accent"
            onClick={confirm}
            disabled={savingBatch}
            style={siengeLiveMatch.inactive ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
          >
            <Icon name="check" size={13} stroke={2.4} /> {savingBatch ? 'A salvar...' : paymentType === 'individual' ? (needsSaveFromSienge ? 'Salvar Credor e Criar Pagamento' : hasIdentificationPending ? 'Criar Pagamento com Pendência' : 'Criar Pagamento') : 'Confirmar pagamentos'}
          </button>
        )}
      </div>

      <Modal open={siengeInactiveModal} onClose={() => setSiengeInactiveModal(false)} width={480}>
        <div className="sp-modal__head">
          <div>
            <div className="sp-modal__head-title">Credor inativo no Sienge</div>
            <div className="sp-modal__head-desc">Pagamento não pode ser criado</div>
          </div>
          <button type="button" className="sp-btn sp-btn-ghost sp-modal__close" onClick={() => setSiengeInactiveModal(false)}>
            <Icon name="x" size={14} />
          </button>
        </div>
        <div className="sp-modal__body sp-modal__body--stack-lg">
          <div className="sp-sidebar-card__muted">
            O credor identificado está com status <strong>inativo</strong> na API do Sienge e não pode receber pagamentos.
          </div>
          <div className="sp-delete-banner">
            Reative o credor no Sienge antes de criar um pagamento para ele.
          </div>
          <div className="sp-modal-actions">
            <button type="button" className="sp-btn sp-btn-accent" onClick={() => setSiengeInactiveModal(false)}>Entendido</button>
          </div>
        </div>
      </Modal>
      <ConfirmModal
        open={!!confirmModal}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmLabel={confirmModal?.confirmLabel}
        onConfirm={() => {
          confirmModal?.onConfirm?.();
          setConfirmModal(null);
        }}
        onCancel={() => setConfirmModal(null)}
      />
    </PShell>
  );
}
